import { ProjectSchema, formatIssues, type Project } from '../domain/schemas';
import { Observable } from '../lib/store';

/**
 * Bounded undo/redo for scene, character and animation edits (Phase 3A, 3B).
 *
 * It records only the *visual slice* of the project: `scenes`, `activeSceneId`,
 * `characters`, `animations` and `avSessions` (Phase 4A). It does this by watching the project for changes to that slice, so every
 * edit path (canvas, inspector, prompts) is covered, and music and audio edits — which
 * never touch these fields — are never recorded and never undone. Undo/redo replace only
 * the slice, after validating the result against the full project schema. A step that
 * no longer fits (for example, it refers to an image that has since been deleted) is
 * refused with a message.
 *
 * A history belongs to one open project and is discarded with it. The camera (zoom/pan),
 * selection, playhead, playback state, timeline zoom and Auto-Key are UI state, not
 * project data, so they are never recorded.
 */
export type VisualSlice = Pick<Project, 'scenes' | 'activeSceneId' | 'characters' | 'animations' | 'avSessions'>;

export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  size: number;
}

interface Step {
  before: VisualSlice;
  after: VisualSlice;
  label: string;
  key: string | null;
  at: number;
}

export interface HistoryTarget {
  get(): Project | null;
  subscribe(listener: () => void): () => void;
  update(mutate: (project: Project) => Project): void;
}

export const HISTORY_LIMIT = 100;
/** Consecutive edits with the same coalesce key within this window become one step. */
export const COALESCE_MS = 1500;

const sliceOf = (p: Project): VisualSlice => ({ scenes: p.scenes, activeSceneId: p.activeSceneId, characters: p.characters, animations: p.animations, avSessions: p.avSessions });
const sameSlice = (a: VisualSlice, b: VisualSlice) =>
  a.scenes === b.scenes && a.activeSceneId === b.activeSceneId && a.characters === b.characters && a.animations === b.animations && a.avSessions === b.avSessions;

export class VisualHistory {
  readonly state = new Observable<HistoryState>({ canUndo: false, canRedo: false, undoLabel: null, redoLabel: null, size: 0 });
  private undoStack: Step[] = [];
  private redoStack: Step[] = [];
  private last: VisualSlice | null = null;
  private applying = false;
  private pendingKey: string | null = null;
  private pendingLabel: string | null = null;
  private unsubscribe: (() => void) | null = null;

  constructor(
    private readonly target: HistoryTarget,
    private readonly limit = HISTORY_LIMIT,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.attach();
  }

  /** Starts watching the project (idempotent; React effects may attach/detach twice). */
  attach(): void {
    if (this.unsubscribe) return;
    const p = this.target.get();
    this.last = p ? sliceOf(p) : null;
    this.unsubscribe = this.target.subscribe(() => this.observe());
  }

  /** Stops watching without dropping the recorded steps. */
  detach(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /**
   * Labels the next recorded change and lets consecutive changes with the same key merge
   * (e.g. typing a number in the inspector). Call right before the update.
   */
  hint(label: string, coalesceKey: string | null = null): void {
    this.pendingLabel = label;
    this.pendingKey = coalesceKey;
  }

  /** Error message when the step can't be applied, or null on success / nothing to do. */
  undo(): string | null {
    return this.move(this.undoStack, this.redoStack, 'before');
  }

  redo(): string | null {
    return this.move(this.redoStack, this.undoStack, 'after');
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    const p = this.target.get();
    this.last = p ? sliceOf(p) : null;
    this.publish();
  }

  dispose(): void {
    this.detach();
    this.undoStack = [];
    this.redoStack = [];
  }

  private observe(): void {
    const p = this.target.get();
    if (!p) return;
    const current = sliceOf(p);
    const previous = this.last;
    if (!previous) {
      this.last = current;
      return;
    }
    if (sameSlice(previous, current)) return;
    this.last = current;
    if (this.applying) return;
    const key = this.pendingKey;
    const label = this.pendingLabel ?? 'Scene edit';
    this.pendingKey = null;
    this.pendingLabel = null;
    const top = this.undoStack[this.undoStack.length - 1];
    const t = this.now();
    if (top && key !== null && top.key === key && t - top.at <= COALESCE_MS) {
      top.after = current;
      top.at = t;
    } else {
      this.undoStack.push({ before: previous, after: current, label, key, at: t });
      if (this.undoStack.length > this.limit) this.undoStack.splice(0, this.undoStack.length - this.limit);
    }
    this.redoStack = [];
    this.publish();
  }

  private move(from: Step[], to: Step[], side: 'before' | 'after'): string | null {
    const step = from[from.length - 1];
    const p = this.target.get();
    if (!step || !p) return null;
    const slice = step[side];
    const candidate = { ...p, ...slice };
    const parsed = ProjectSchema.safeParse(candidate);
    if (!parsed.success) {
      return `Can’t ${side === 'before' ? 'undo' : 'redo'} “${step.label}”: ${formatIssues(parsed.error)[0] ?? 'the result would be invalid'}`;
    }
    from.pop();
    to.push(step);
    this.applying = true;
    try {
      this.target.update((project) => ({ ...project, ...slice }));
    } finally {
      this.applying = false;
    }
    const after = this.target.get();
    this.last = after ? sliceOf(after) : null;
    this.publish();
    return null;
  }

  private publish(): void {
    const u = this.undoStack[this.undoStack.length - 1];
    const r = this.redoStack[this.redoStack.length - 1];
    this.state.set({ canUndo: !!u, canRedo: !!r, undoLabel: u?.label ?? null, redoLabel: r?.label ?? null, size: this.undoStack.length });
  }
}
